/**
 * Optimized image variant configurations
 * Designed to reduce storage costs while maintaining quality for different use cases
 */

export interface PhotoVariantConfig {
  width: number;
  height: number;
  quality: number;
  format: 'jpeg' | 'webp';
  useCase: string;
  /**
   * `contain` (default) fits the image inside the box, preserving the whole
   * frame. `cover` fills the box and centre-crops the overflow.
   *
   * The thumbnail is `cover` because every consumer already displays it as a
   * circle with CSS `object-cover` / RN `resizeMode="cover"`, which shows
   * exactly the centred square and throws the rest away at render time. Doing
   * the crop here changes no framing — it just stops us storing pixels nothing
   * can display, and spending the budget on the part that IS displayed.
   */
  fit?: 'contain' | 'cover';
}

import { IMAGE_CONFIG } from '@/lib';

// Optimized variants - reduced from original 3 sizes to better targeted sizes
export const OPTIMIZED_PHOTO_VARIANTS = {
  // For list views, thumbnails, avatars. Square centre-crop — see `fit`.
  thumbnail: {
    width: IMAGE_CONFIG.THUMBNAIL_SIZE,
    height: IMAGE_CONFIG.THUMBNAIL_SIZE,
    quality: IMAGE_CONFIG.THUMBNAIL_QUALITY,
    format: 'jpeg' as const,
    fit: 'cover' as const,
    useCase: 'List views, avatars, quick previews'
  },
  
  // For detail views, profiles (high quality for viewing)
  display: {
    width: IMAGE_CONFIG.DISPLAY_SIZE,
    height: IMAGE_CONFIG.DISPLAY_SIZE,
    quality: IMAGE_CONFIG.DISPLAY_QUALITY,
    format: 'jpeg' as const,
    useCase: 'Profile pages, detail views, modals'
  },
  
  // Original quality for archival/printing (compressed but high quality)
  archive: {
    width: IMAGE_CONFIG.ARCHIVE_SIZE,
    height: IMAGE_CONFIG.ARCHIVE_SIZE,
    quality: IMAGE_CONFIG.ARCHIVE_QUALITY,
    format: 'jpeg' as const,
    useCase: 'High quality display, printing, archival'
  }
} as const;

// Legacy mapping for backward compatibility
export const LEGACY_VARIANT_MAPPING = {
  'thumbnail': 'thumbnail',
  'large': 'display',
  'original': 'archive'
} as const;

export type OptimizedVariantType = keyof typeof OPTIMIZED_PHOTO_VARIANTS;
export type LegacyVariantType = keyof typeof LEGACY_VARIANT_MAPPING;

/**
 * The source rectangle to draw for a `cover` fit: the largest centred square
 * (or box of the target aspect ratio) that fits inside the source.
 *
 * Mirrors CSS `object-fit: cover` with the default `object-position: 50% 50%`,
 * which is what every thumbnail consumer already applies at render time.
 */
export function calculateCoverCrop(
  originalWidth: number,
  originalHeight: number,
  targetWidth: number,
  targetHeight: number
): { sx: number; sy: number; sWidth: number; sHeight: number } {
  const targetRatio = targetWidth / targetHeight;
  const sourceRatio = originalWidth / originalHeight;

  let sWidth = originalWidth;
  let sHeight = originalHeight;

  if (sourceRatio > targetRatio) {
    // Source is wider than the target box — trim the sides.
    sWidth = originalHeight * targetRatio;
  } else if (sourceRatio < targetRatio) {
    // Source is taller — trim top and bottom.
    sHeight = originalWidth / targetRatio;
  }

  return {
    sx: (originalWidth - sWidth) / 2,
    sy: (originalHeight - sHeight) / 2,
    sWidth,
    sHeight,
  };
}

/**
 * Output dimensions for a `cover` fit, never enlarging.
 *
 * A source whose crop is smaller than the target yields the crop's own size
 * rather than an upscale: hok-mobile accepts uploads down to 150px, and
 * inventing pixels would only produce a bigger file that looks no better.
 */
export function calculateCoverDimensions(
  originalWidth: number,
  originalHeight: number,
  targetWidth: number,
  targetHeight: number
): { width: number; height: number } {
  const { sWidth, sHeight } = calculateCoverCrop(
    originalWidth,
    originalHeight,
    targetWidth,
    targetHeight
  );

  const scale = Math.min(1, targetWidth / sWidth, targetHeight / sHeight);

  return {
    width: Math.max(1, Math.round((sWidth * scale) / 2) * 2),
    height: Math.max(1, Math.round((sHeight * scale) / 2) * 2),
  };
}

/**
 * Calculate optimal dimensions while preserving aspect ratio
 */
export function calculateOptimalDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  // If image is already smaller, don't upscale
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }

  const aspectRatio = originalWidth / originalHeight;
  
  let newWidth = maxWidth;
  let newHeight = maxWidth / aspectRatio;
  
  // If height exceeds max, scale by height instead
  if (newHeight > maxHeight) {
    newHeight = maxHeight;
    newWidth = maxHeight * aspectRatio;
  }
  
  // Round to even numbers for better compression
  return {
    width: Math.round(newWidth / 2) * 2,
    height: Math.round(newHeight / 2) * 2
  };
}

/**
 * Get variant configuration by type
 */
export function getVariantConfig(variant: OptimizedVariantType): PhotoVariantConfig {
  return OPTIMIZED_PHOTO_VARIANTS[variant];
}

/**
 * Get all variant types
 */
export function getAllVariantTypes(): OptimizedVariantType[] {
  return Object.keys(OPTIMIZED_PHOTO_VARIANTS) as OptimizedVariantType[];
}

/**
 * Map legacy variant type to optimized variant
 */
export function mapLegacyVariant(legacyVariant: string): OptimizedVariantType {
  const mapped = LEGACY_VARIANT_MAPPING[legacyVariant as LegacyVariantType];
  return mapped || 'display'; // Default to display if unknown
}

/**
 * Check if image needs resizing based on variant config
 */
export function needsResizing(
  originalWidth: number,
  originalHeight: number,
  variant: OptimizedVariantType
): boolean {
  const config = getVariantConfig(variant);
  return originalWidth > config.width || originalHeight > config.height;
}

/**
 * Calculate compression ratio for a variant
 */
export function calculateCompressionRatio(
  originalSize: number,
  compressedSize: number
): number {
  return Math.round(((originalSize - compressedSize) / originalSize) * 100);
} 